/* @flow */
import * as React from 'react';
import { withTranslation } from 'react-i18next';

import Styled from './styled';
import Slider from '../Slider';

import type { Translate } from '../../types';

type Props = {
  title?: React.Node,
  items: any[],
  initialSlide?: number,
  render: (item: any) => React.Element<any>,
  renderMissing?: () => React.Element<any>,
  renderNoData?: () => React.Element<any>,
  autoplay: boolean,
  onExpandClick?: (event: any) => any,
  onCollapseClick?: (event: any) => any,
  onSlideChange?: (slideIndex: number) => any,
  isExpanded?: boolean,
  t: Translate,
};

class SliderList extends React.PureComponent<Props, void> {
  _renderItem = item => {
    const { render } = this.props;
    if (typeof render === 'function') {
      return render(item);
    } else {
      return this._renderMissingItem();
    }
  };

  _renderMissingItem = () => {
    const { renderMissing } = this.props;
    if (typeof renderMissing === 'function') {
      return renderMissing();
    } else {
      return <div />;
    }
  };

  _renderNoData = () => {
    const { renderNoData, t } = this.props;
    if (renderNoData) {
      return renderNoData();
    }
    return <Styled.Centered>{t('NO_RESULT')}</Styled.Centered>;
  };

  _handleSlideChange = (oldIndex: number, newIndex: number) => {
    const { onSlideChange } = this.props;
    onSlideChange && onSlideChange(newIndex);
  };

  render() {
    const {
      title,
      items,
      initialSlide = 0,
      isExpanded = false,
      onExpandClick,
      onCollapseClick,
      t,
    } = this.props;
    let { autoplay = false } = this.props;
    const slidesToShow = 6;
    let missingSlidesCount = slidesToShow + 1;
    if (items && items.length > 0) {
      missingSlidesCount -= items.length;
    }
    autoplay = missingSlidesCount > 0 ? false : autoplay;
    const canShowMore = missingSlidesCount <= 0 && !isExpanded;
    const canShowLess = items.length > 6 && isExpanded;
    const isHeaderVisible =
      title || (!isExpanded && onExpandClick) || (isExpanded && onCollapseClick);

    return (
      <Styled.SliderSection>
        {isHeaderVisible && (
          <Styled.Header>
            <Styled.Title>{title}</Styled.Title>
            {canShowMore && onExpandClick && (
              <Styled.ShowMoreButton onClick={onExpandClick}>
                {t('SHOW_MORE')}
              </Styled.ShowMoreButton>
            )}
            {canShowLess && onCollapseClick && (
              <Styled.ShowMoreButton onClick={onCollapseClick}>
                {t('SHOW_LESS')}
              </Styled.ShowMoreButton>
            )}
          </Styled.Header>
        )}
        <Styled.SliderWrapper>
          {isExpanded ? (
            <Styled.GridSlider>
              {items && items.length > 0
                ? items.map((item: any, index: number) => (
                    <div key={index}>
                      <Styled.SlideWrapper>{this._renderItem(item)}</Styled.SlideWrapper>
                    </div>
                  ))
                : this._renderNoData()}
            </Styled.GridSlider>
          ) : (
            <Slider
              speed={250}
              slidesToShow={6}
              slidesToScroll={5}
              initialSlide={initialSlide}
              easing={'linear'}
              pauseOnFocus={true}
              lazyLoad={true}
              centerMode={false}
              autoplay={autoplay}
              arrows={canShowMore}
              draggable={canShowMore}
              beforeChange={this._handleSlideChange}
            >
              {items && items.length > 0
                ? items.map((item: any, index: number) => (
                    <div key={index}>
                      <Styled.SlideWrapper isFirstSlide={index === 0}>
                        {this._renderItem(item)}
                      </Styled.SlideWrapper>
                    </div>
                  ))
                : this._renderNoData()}
              {missingSlidesCount > 0 &&
                Array(missingSlidesCount)
                  .fill({})
                  .map((_, index) => (
                    <div key={items.length + index}>
                      <Styled.SlideWrapper>{this._renderMissingItem()}</Styled.SlideWrapper>
                    </div>
                  ))}
            </Slider>
          )}
        </Styled.SliderWrapper>
      </Styled.SliderSection>
    );
  }
}

export default withTranslation()(SliderList);
